const DEFAULT_WORK_START_HOUR = 8;
const DEFAULT_WORK_END_HOUR = 20;
const DEFAULT_BOOKING_STEP_MINUTES = 60;
const DEFAULT_APPOINTMENT_DURATION_MINUTES = 60;

// Modifier ces valeurs pour personnaliser les horaires et le pas de l'agenda.
const RAW_CALENDAR_SETTINGS = {
  workStartHour: DEFAULT_WORK_START_HOUR,
  workEndHour: DEFAULT_WORK_END_HOUR,
  bookingStepMinutes: DEFAULT_BOOKING_STEP_MINUTES,
  defaultAppointmentDurationMinutes: DEFAULT_APPOINTMENT_DURATION_MINUTES,
} as const;

const parseInteger = (rawValue: string | undefined, fallback: number): number => {
  if (!rawValue) {
    return fallback;
  }

  const parsed = Number.parseInt(rawValue, 10);
  return Number.isNaN(parsed) ? fallback : parsed;
};

const clampInteger = (value: number, min: number, max: number): number => {
  if (value < min) {
    return min;
  }

  if (value > max) {
    return max;
  }

  return value;
};

const rawWorkStartHour = parseInteger(
  String(RAW_CALENDAR_SETTINGS.workStartHour),
  DEFAULT_WORK_START_HOUR
);

const rawWorkEndHour = parseInteger(
  String(RAW_CALENDAR_SETTINGS.workEndHour),
  DEFAULT_WORK_END_HOUR
);

const workStartHour = clampInteger(rawWorkStartHour, 0, 22);
const minEndHour = workStartHour + 1;
const workEndHour = clampInteger(rawWorkEndHour, minEndHour, 23);

const rawBookingStepMinutes = parseInteger(
  String(RAW_CALENDAR_SETTINGS.bookingStepMinutes),
  DEFAULT_BOOKING_STEP_MINUTES
);

const bookingStepMinutes = rawBookingStepMinutes > 0 && rawBookingStepMinutes <= 60 && 60 % rawBookingStepMinutes === 0
  ? rawBookingStepMinutes
  : DEFAULT_BOOKING_STEP_MINUTES;

const rawDefaultAppointmentDurationMinutes = parseInteger(
  String(RAW_CALENDAR_SETTINGS.defaultAppointmentDurationMinutes),
  DEFAULT_APPOINTMENT_DURATION_MINUTES
);

const defaultAppointmentDurationMinutes = rawDefaultAppointmentDurationMinutes > 0
  ? rawDefaultAppointmentDurationMinutes
  : DEFAULT_APPOINTMENT_DURATION_MINUTES;

const getTimeLabel = (hours: number, minutes: number = 0): string => {
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

const buildStartTimeOptions = (): string[] => {
  const options: string[] = [];
  const startMinutes = workStartHour * 60;
  const latestStartMinutes = workEndHour * 60 - bookingStepMinutes;

  for (let minutes = startMinutes; minutes <= latestStartMinutes; minutes += bookingStepMinutes) {
    const hours = Math.floor(minutes / 60);
    const minutePart = minutes % 60;
    options.push(getTimeLabel(hours, minutePart));
  }

  return options;
};

const roundDateToBookingStep = (date: Date): Date => {
  const stepMs = bookingStepMinutes * 60 * 1000;
  return new Date(Math.round(date.getTime() / stepMs) * stepMs);
};

const clampDateToWorkingRange = (date: Date): Date => {
  const min = new Date(date);
  min.setHours(workStartHour, 0, 0, 0);

  const max = new Date(date);
  max.setHours(workEndHour, 0, 0, 0);
  max.setMinutes(max.getMinutes() - bookingStepMinutes);

  if (date < min) {
    return min;
  }

  if (date > max) {
    return max;
  }

  return date;
};

export const CALENDAR_SETTINGS = {
  workStartHour,
  workEndHour,
  bookingStepMinutes,
  defaultAppointmentDurationMinutes,
  workEndHourLabel: getTimeLabel(workEndHour),
  startTimeOptions: buildStartTimeOptions(),
} as const;

export { clampDateToWorkingRange, roundDateToBookingStep };
